<script setup lang="ts">
import type { InputHTMLAttributes } from 'vue'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
interface IProps extends /* @vue-ignore */ InputHTMLAttributes {
  modelValue?: string | number | null
  id?: string
  error?: string
  disabled?: boolean
  class?: string
  inputClass?: string
  trasformers?: ((value: string) => string)[]
  focus?: () => void
}

interface IEmits {
  (event: 'update:modelValue', value: string | number | null): void
}

const props = defineProps<IProps>()
const emit = defineEmits<IEmits>()
const uniqueId = randomID()

defineOptions({
  name: 'FormInput',
})

const value = useVModel(props, 'modelValue', emit)
const handlerTransforms = (data: string) => {
  const transformList = [(v: string) => v, ...(props.trasformers ?? [])]
  const finalValue = transformList.reduce((acc, fn) => fn(acc), data)
  value.value = finalValue
}

onMounted(() => {
  if (props.modelValue)
    handlerTransforms(props.modelValue.toString() as string)
})

const updateValue = (event: Event) => {
  handlerTransforms((event.target as HTMLInputElement).value)
}

const inputRef = ref<HTMLInputElement | null>(null)

const enterEvent = (event: Event) => {
  event.preventDefault()
  inputRef.value?.blur()
}

defineExpose({
  focus: () => {
    inputRef.value?.focus()
  },
  blur: () => {
    inputRef.value?.blur()
  },
})
</script>

<template>
  <Popover :open="!!error" :arrow="true" class="w-full">
    <slot name="label" />
    <PopoverTrigger as="label" :for="`${id}-${uniqueId}`" flex="flex flex-col w-full flex-grow gap-2">
      <div
        class="w-full px-3 flex items-center gap-2 min-h-10 bg-gray-lighter border-1px border-gray-lighter rounded-2 text-sm color-bluish-darker "

        :class="[$style.input, (disabled && $style.backgroundDisabled), error && $style.error, props.class || '']"
      >
        <span v-if="$slots.prefix" class="flex items-center">
          <slot name="prefix" />
        </span>
        <input
          :id="`${id}-${uniqueId}`"
          ref="inputRef"
          :value="value"
          :disabled="disabled"
          :class="[$style.input, error && $style.error, inputClass || '']"
          v-bind="$attrs"
          class="w-full bg-transparent min-w-0 py-3"
          @input="updateValue($event)"
          @keydown.enter="enterEvent"
        >

        <span v-if="$slots.suffix" class="flex items-center">
          <slot name="suffix" />
        </span>
      </div>
    </PopoverTrigger>

    <PopoverContent align="start">
      <p class="text-red-500">
        {{ error }}
      </p>
    </PopoverContent>
  </Popover>
</template>

<style lang="scss" module>
.backgroundDisabled {
  background: #E3E9F8;
}

.input:focus-visible {
  outline: none;
}

.input::placeholder {
  @apply text-dark-gray;
  font-size: 14px;
  line-height: 20px;
}

.error {
  @apply border-red-500 text-red-500;
}
</style>
